import React, { ChangeEvent, useCallback, useState } from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { LinkButton, StandardButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { SummaryCard } from '@eika/card';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { Input } from '@eika/input';
import { ErrorLabel } from '@eika/label';
import { RichIconTypes } from '@eika/rich-icon';

import { useAccount } from '../../fetchHooks/useAccount';
import { useValidateName } from '../../fetchHooks/useAuthorizedCustomers';
import type { AccountId } from '../../generated-types';
import useTranslation, { formatJsx } from '../../hooks/useTranslation';
import SigningContextFlow from '../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../signingContext/signingContext';
import { accountTrackingIDs } from '../../utils/gtm/trackIDs/account';
import ActionResultInfo from '../shared/ActionResultInfo/ActionResultInfo';
import AuthorizedCustomerTerms from './AuthorizedCustomerTerms';
import isValidInput from './utils';

import './AddAuthorizedCustomerModal.scss';

export const ANALYTICS_CATEGORY = 'authorized-customer';

interface Props {
    accountId: AccountId;
    setCustomerSsid: (value: string) => void;
    setCustomerName: (value: string) => void;
    onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
    customerName: string;
    customerSsid: string;
    addAuthorizedCustomerLoading: boolean;
    addAuthorizedCustomerError?: RequestExceptionWithResponse;
    onClose: () => void;
}

const formatSSID = (ssid: string, seperator = ' ') => {
    return [ssid.slice(0, 6), ssid.slice(6, 11)].join(seperator);
};

const AddAuthorizedCustomerForm: React.FC<Props> = ({
    accountId,
    setCustomerName,
    customerName,
    setCustomerSsid,
    customerSsid,
    onSubmit,
    addAuthorizedCustomerLoading,
    addAuthorizedCustomerError,
    onClose,
}) => {
    const translated = useTranslation().accountDetails.addAuthorizedCustomer;
    const { signingStatus, signingError, handleCancelSigningFlow } = useSigningContext();
    const { data: account } = useAccount(accountId);
    const [notCustomerError, setNotCustomerError] = useState(false);
    const [validationError, setValidationError] = useState(false);
    const [stage, setStage] = useState<'initial' | 'confirm' | 'terms'>('initial');

    const errorNameValidation = (error: RequestExceptionWithResponse) => {
        if (error.errorName === 'USER_NOT_CUSTOMER') setNotCustomerError(true);
        else setValidationError(true);
    };
    const successNameValidation = (success: boolean) => {
        if (success) setStage('confirm');
        else setValidationError(!success);
    };
    const validateName = useValidateName(
        accountId,
        { value: customerSsid },
        customerName,
        errorNameValidation,
        successNameValidation,
    );

    const validateInput = () => {
        setNotCustomerError(false);
        if (!isValidInput(customerSsid, customerName)) setValidationError(true);
        else {
            setValidationError(false);
            validateName.mutateAsync();
        }
    };

    const closeAndCancelSigning = useCallback(() => {
        handleCancelSigningFlow();
        onClose();
    }, [handleCancelSigningFlow, onClose]);

    if (signingStatus === 'COMPLETED' || signingError || addAuthorizedCustomerError) {
        return (
            <ActionResultInfo
                title={translated.shareSuccess}
                error={signingError || addAuthorizedCustomerError}
                retry={onClose}
                icon={<CheckmarkAnimation />}
                successButton={<StandardButton onClick={onClose}>{translated.doneButton}</StandardButton>}
            />
        );
    }

    return (
        <SigningContextFlow onClosePayment={closeAndCancelSigning}>
            <form className="add-authorized-customer__form" onSubmit={onSubmit}>
                {stage === 'terms' && (
                    <>
                        <AuthorizedCustomerTerms />
                        <div className="add-authorized-customer__button-container">
                            <ButtonWithSpinner
                                buttonType="positive"
                                showSpinner={addAuthorizedCustomerLoading}
                                type="submit"
                                data-track-id={accountTrackingIDs.addAuthorizedCustomer.id}
                                data-track-linkname="submit"
                            >
                                {translated.shareAccountButton}
                            </ButtonWithSpinner>
                            <LinkButton
                                onClick={() => {
                                    setStage('initial');
                                    onClose();
                                }}
                                data-track-id={accountTrackingIDs.addAuthorizedCustomer.id}
                                data-track-linkname="navigate__close"
                            >
                                {translated.deleteCancelButton}
                            </LinkButton>
                        </div>
                    </>
                )}
                {stage === 'confirm' && (
                    <>
                        <h2 className="typography-h1__light">{translated.confirmTitle}</h2>
                        <SummaryCard
                            icon={RichIconTypes.HJERTE_M_HENDER}
                            className="add-authorized-customer__confirm-card"
                        >
                            <p className="typography-paragraph">
                                {formatJsx(
                                    translated.confirmText,
                                    <span className="bold">
                                        {customerName}, {formatSSID(customerSsid)}
                                    </span>,
                                    <span>{account?.alias}</span>,
                                    <span>{formatAccountNumber(account?.accountNumber)}</span>,
                                )}
                            </p>
                        </SummaryCard>
                        <div className="add-authorized-customer__button-container">
                            <ButtonWithSpinner
                                buttonType="positive"
                                showSpinner={addAuthorizedCustomerLoading}
                                onClick={() => setStage('terms')}
                                data-track-id={accountTrackingIDs.addAuthorizedCustomer.id}
                                data-track-linkname="navigate__terms"
                            >
                                {translated.nextButton}
                            </ButtonWithSpinner>
                            <LinkButton
                                onClick={() => setStage('initial')}
                                data-track-id={accountTrackingIDs.addAuthorizedCustomer.id}
                                data-track-linkname="navigate__initial"
                            >
                                {translated.backButton}
                            </LinkButton>
                        </div>
                    </>
                )}
                {stage === 'initial' && (
                    <>
                        <h2 className="typography-h1__light">{translated.shareAccount}</h2>
                        <span className="account-details__form-label">{translated.chooseWho}</span>
                        <span className="account-details__form-sublabel">{translated.mustBeCustomer}</span>
                        <Input
                            label={translated.ssid}
                            placeholder={translated.elevenCharacters}
                            value={customerSsid}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => setCustomerSsid(event.target.value)}
                            error={validationError ? translated.ssidError : ''}
                            type="tel"
                        />
                        <Input
                            label={translated.fillName}
                            placeholder={translated.fullname}
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                            error={validationError ? translated.nameError : ''}
                        />
                        {notCustomerError && <ErrorLabel text={translated.mustBeCustomerWarning} />}
                        <div className="add-authorized-customer__button-container">
                            <ButtonWithSpinner
                                buttonType="positive"
                                showSpinner={validateName.isPending}
                                onClick={validateInput}
                                data-track-id={accountTrackingIDs.addAuthorizedCustomer.id}
                                data-track-linkname="validate__input"
                            >
                                {translated.nextButton}
                            </ButtonWithSpinner>
                        </div>
                    </>
                )}
            </form>
        </SigningContextFlow>
    );
};

export default AddAuthorizedCustomerForm;
